/**
 * @file planForm.ts
 * @brief The Plans & seats form shared by the card rows and the Edit plan
 *        sheet, and their plain error / tooltip text
 *
 * @author DES (prepared with Claude)
 * @date 22-10-2026
 */

import { HOURS_KEYS, minFor, readable, zeroMeaning, type FormValues, type PlanSetting } from "./launchPlans";

/** The form the rows and the side sheet edit together. */
export interface PlanForm {
  value: (key: string) => string;
  set: (key: string, v: string) => void;
  baseline: FormValues;
  invalid: ReadonlySet<string>;
}

/** Bare-field error text; replaces the label while the value can't be saved. */
export function fieldError(s: PlanSetting): string {
  if (s.kind === "minor") return "Enter an amount, e.g. 14.99";
  if (s.kind === "choice") return "Pick one";
  const zero = zeroMeaning(s.key);
  if (HOURS_KEYS.has(s.key)) {
    return zero ? `Enter hours, or 0 (${zero.toLowerCase()})` : "Enter hours, at least 1 minute";
  }
  // B20: 0 means something on these (never, unlimited, no maximum).
  if (zero) return `Enter a whole number, or 0 (${zero.toLowerCase()})`;
  if (s.kind === "seconds") return `Enter seconds, ${minFor(s)} or more`;
  if (s.kind === "days" || s.kind === "seats") return "Enter a whole number, 1 or more";
  if (s.kind === "limit") return "Enter a whole number, or Unlimited";
  return "Enter a whole number, 0 or more";
}

/** Tooltip text: the key, whether it is a code default, what it was. */
export function keyTip(s: PlanSetting, form: PlanForm): string {
  const parts = [s.key];
  if (s.value === null) parts.push("default, not saved yet");
  const before = form.baseline[s.key] ?? "";
  if (form.value(s.key) !== before) parts.push(`was ${readable(s, before)}`);
  return parts.join(" · ");
}
